import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Col, FloatingLabel, Form, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import Button from '../../../components/base/Button';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import useFetchActiveProject from '../../../hooks/useFetchActiveProject';
import InventoryTab from '../../../components/tabs/InventoryTab';
import { setToast } from '../../../components/Toasts/data-access/toast-reducer';
import { ProjectCreateActions } from './data-access/+state/project-create.actions';
import { ProjectCreationSelectors } from './data-access/+state/project-create.selectors';
import { Domain } from '@shared/types/project.type';
import api from '../../../app/api/axios-api';
import { ProjectSelector } from '../project-list/data-access/+state/project-list.selectors';
import { fetchCSAPlusProject, updateCSAPlusProject } from '../../csa-plus/data-access/csa-plus.api';
import { getFirstDimensionId } from 'helpers/utils';

const toProjectDetailsPath = (projectId: string) =>
  `/project/details-tabs?dimension-id=${getFirstDimensionId()}&projectId=${encodeURIComponent(projectId)}`;

const UpdateProject = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId: urlProjectId } = useParams<{ projectId?: string }>();
  const { pathname } = useLocation();
  const userId = localStorage.getItem('userId');
  const isCSAPlus = pathname.includes('/csa+/');

  // State for form inputs
  const [title, setTitle] = useState('');
  const [overview, setOverview] = useState('');
  const [domain, setDomain] = useState<Domain>('Banking');
  const [projectId, setProjectId] = useState<string | null>(null);

  const [isCompleteTriggered, setIsCompleteTriggered] = useState(false);
  const [activeApi, setActiveApi] = useState<string | null>('');
  useFetchActiveProject();
  const activeProject = useSelector(ProjectSelector.activeProject);

  const updateProjectResponse = useSelector(ProjectCreationSelectors.updateProjectResponse);

  const [activeTab, setActiveTab] = useState<string | null>('create');
  const [isUpdatingCSA, setIsUpdatingCSA] = useState(false);

  // CSA+ flow: fetch project by URL projectId and prepopulate
  useEffect(() => {
    if (isCSAPlus && urlProjectId) {
      fetchCSAPlusProject(urlProjectId)
        .then(p => {
          if (p) {
            setProjectId(p.projectId ?? urlProjectId);
            setTitle(p.projectTitle ?? '');
            setOverview(p.description ?? '');
            setDomain((p.domainName as Domain) || 'Banking');
          }
        })
        .catch(() => {});
    }
  }, [isCSAPlus, urlProjectId]);

  // Sync `activeProject` values with the state when it changes (non-CSA+ flow)
  useEffect(() => {
    if (!isCSAPlus && activeProject) {
      setProjectId(activeProject.projectId);
      setTitle(activeProject.title || '');
      setOverview(activeProject.overview || '');
      setDomain(activeProject.domain || 'Banking');
    }
  }, [isCSAPlus, activeProject]);

  const onComplete = async () => {
    if (activeProject) {
      const meaid = uuidv4();
      const requestPayload = {
        projectId: activeProject.projectId,
        userId: userId,
        domain: activeProject.domain,
        meaid: meaid,
        is_rerun: false
      };

      setIsCompleteTriggered(true);
      try {
        setActiveApi('Executing Bias Detection');
        await api.post('/project/features/bias-detection', requestPayload);

        setActiveApi('Executing Fairness Matrix');
        await api.post('/project/features/fairness-matrix', requestPayload);

        setIsCompleteTriggered(false);
        setActiveApi('');
        dispatch(
          setToast({
            show: true,
            title: 'Process Completed',
            message: 'All steps executed successfully'
          })
        );
        navigate(toProjectDetailsPath(activeProject.projectId));
      } catch (error) {
        console.error('Error during API execution:', error);
        setIsCompleteTriggered(false);
        setActiveApi(null);
        dispatch(
          setToast({
            show: true,
            title: 'Execution Failed',
            message: 'An error occurred while executing the process'
          })
        );
      }
    }
  };

  const handleCancel = () => {
    if (isCSAPlus && urlProjectId) {
      navigate(`/csa+/project-details/${encodeURIComponent(urlProjectId)}`);
    } else if (activeProject?.projectId) {
      navigate(toProjectDetailsPath(activeProject.projectId));
    } else {
      navigate(-1);
    }
  };

  const onSubmit = async () => {
    if (isCSAPlus && urlProjectId) {
      setIsUpdatingCSA(true);
      try {
        await updateCSAPlusProject({
          projectId: urlProjectId,
          projectTitle: title || undefined,
          description: overview || undefined,
          domainName: domain || undefined,
          userId: userId || undefined
        });
        dispatch(
          setToast({
            show: true,
            title: 'Project updated',
            message: 'Project has been updated successfully.'
          })
        );
        navigate(`/csa+/project-details/${encodeURIComponent(urlProjectId)}`);
      } catch (err: any) {
        dispatch(
          setToast({
            show: true,
            title: 'Update failed',
            message: err?.response?.data?.message || err?.message || 'Could not update project.'
          })
        );
      } finally {
        setIsUpdatingCSA(false);
      }
      return;
    }

    const organizationId = localStorage.getItem('organizationId');
    if (!organizationId) {
      dispatch(
        setToast({
          show: true,
          title: 'Missing Organization',
          message: 'Organization ID is missing. Please sign in again or contact support.'
        })
      );
      return;
    }

    if (userId && projectId) {
      dispatch(
        ProjectCreateActions.updateProjectAttempt({
          projectId,
          userId,
          title,
          overview,
          domain,
          organizationId
        })
      );
    } else {
      dispatch(
        setToast({
          show: true,
          title: 'Failed',
          message: 'Failed to fetch active project'
        })
      );
    }
  };

  useEffect(() => {
    if (updateProjectResponse === 'succeeded') {
      dispatch(setToast({ show: true, title: 'Project Created', message: 'Success' }));
      setActiveTab('uploads');
    } else if (updateProjectResponse === 'failed') {
      dispatch(setToast({ show: true, title: 'Failed', message: 'Failed to create' }));
    }
  }, [updateProjectResponse]);

  return (
    <div>
      <h2 className="mb-4">Create a project</h2>
      <Row>
        <Col xs={12} xl={10}>
          <Tab.Container id="basic-tabs-example" activeKey={activeTab ?? 'create'} onSelect={k => setActiveTab(k)}>
            <Nav variant="underline" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="create">Project Information</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="uploads">Uploads</Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="create">
                <Row as="form" className="g-3 mb-6">
                  <Col sm={6} md={8}>
                    <FloatingLabel controlId="floatingInputGrid" label="Project title">
                      <Form.Control
                        type="text"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="Project title"
                      />
                    </FloatingLabel>
                  </Col>
                  <Col sm={6} md={4}>
                    <FloatingLabel controlId="floatingSelectTask" label="Select Domain">
                      <Form.Select value={domain} onChange={e => setDomain(e.target.value as Domain)}>
                        <option>Select Domain</option>
                        <option value="Banking">Banking</option>
                        <option value="Insurance">Insurance</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="LifeSciences">Life Sciences</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Pharma">Pharma</option>
                        <option value="CPG">CPG</option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>

                  <Col xs={12} className="gy-6">
                    <FloatingLabel controlId="floatingProjectOverview" label="Project overview">
                      <Form.Control
                        as="textarea"
                        value={overview}
                        onChange={e => setOverview(e.target.value)}
                        placeholder="Leave a comment here"
                        style={{ height: '100px' }}
                      />
                    </FloatingLabel>
                  </Col>

                  <Col xs={12} className="gy-6">
                    <div className="d-flex justify-content-end gap-3">
                      <Button variant="phoenix-primary" className="px-5" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button onClick={onSubmit} variant="phoenix-primary" className="px-5" disabled={isUpdatingCSA}>
                        {isUpdatingCSA ? (
                          <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                        ) : updateProjectResponse === 'loading' ? (
                          <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                        ) : (
                          'Update'
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Tab.Pane>
              <Tab.Pane eventKey="uploads">
                <InventoryTab uploadMode={isCSAPlus ? 'full' : 'tsc'} />
                <div className="d-flex justify-content-end gap-3 mt-3">
                  <Button variant="phoenix-primary" className="px-5">
                    Skip
                  </Button>
                  <Button variant="phoenix-primary" className="px-5" onClick={onComplete}>
                    Complete
                  </Button>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Col>
      </Row>
      <Backdrop sx={theme => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={isCompleteTriggered}>
        <CircularProgress color="inherit" />
        <p>{activeApi}</p>
      </Backdrop>
    </div>
  );
};

export default UpdateProject;
